import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CircularProgress, Grid, Tab, Tabs, Typography } from "@material-ui/core";
import { FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { getUserBelowById, showErrorMessage } from "../actions";
import UserContractsOverview from "./components/UserContractsOverview";
import AbtcAvatar from "../custom/Avatar";
import FixedPageItem from "../custom/FixedPageItem";
import HeaderPageItem from "../custom/HeaderPageItem";
import PageContainer, { CardPageItem } from "../custom/Page";
import { resolveErrorMessage, Roles } from "../util";
import { withRouter } from "react-router-dom";
import UserDetailVIew from "./components/Management/UserDetailPage/UserDetailView";

const UserDetailPage = ({ match, location, history }) => {
  const locationState = location?.state;
  const currentUser = useSelector((state) => state.user.user.me);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [indexTab, setIndexTab] = useState(0);
  const [user, setUser] = useState(null);

  const handleError = useCallback(
    (mode, message) => {
      if (mode === "RENDER") {
        setError(message);
        return;
      }
      if (mode === "TOAST") {
        dispatch(showErrorMessage(message));
        return;
      }
    },
    [dispatch],
  );

  const fetchUser = useCallback(
    async (mode = "TOAST") => {
      try {
        setError(false);
        const { id } = match.params;
        if (!/^\d+$/.test(id)) throw new Error("app.user.invalidId");
        if (currentUser.id.toString() === id.toString()) {
          throw new Error("app.user.notFound");
        }
        const data = await getUserBelowById(id);
        setUser(data);
      } catch (e) {
        const errorMessage = resolveErrorMessage(e);
        handleError(mode, errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [match.params, currentUser.id, handleError],
  );

  useEffect(() => {
    fetchUser("RENDER");
  }, [fetchUser]);

  const hasBackTo = locationState && Object.keys(locationState).length > 0 && Boolean(locationState.backTo);

  const handleOnBack = () => {
    hasBackTo ? history.push(locationState?.backTo, locationState?.filter ?? {}) : history.goBack();
  };

  if (loading) {
    return (
      <PageContainer>
        <HeaderPageItem
          title="app.customerInfo"
          onBack={handleOnBack}
        />
        <CardPageItem>
          <div style={{ textAlign: "center", marginTop: 25 }}>
            <CircularProgress />
          </div>
        </CardPageItem>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <HeaderPageItem
          title="app.customerInfo"
          onBack={handleOnBack}
        />

        <CardPageItem>
          <CardContent>
            <Typography
              variant="h6"
              align="center"
              color="error"
              paragraph
            >
              <FormattedMessage
                id={error}
                defaultMessage={error}
              />
            </Typography>

            <Typography
              variant="body1"
              align="center"
              color="textSecondary"
              paragraph
            >
              <FormattedMessage id="app.user.unableToDisplay" />
            </Typography>
          </CardContent>
        </CardPageItem>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <HeaderPageItem
        title="app.customerInfo"
        onBack={handleOnBack}
      />

      <FixedPageItem
        position="top"
        variant="sticky"
      >
        <Card>
          <CardHeader
            avatar={
              <AbtcAvatar
                imageUrl={user?.photoUrl}
                disableLink
              />
            }
            title={`${user?.name} (${user?.id})`}
            subheader={
              <Grid
                container
                justify="space-between"
                alignItems="center"
              >
                <Grid
                  item
                  xs={9}
                >
                  {user.email}{" "}
                  {!user.apt && (
                    <Typography
                      variant="caption"
                      color="error"
                    >
                      <FormattedMessage id="app.inapt" />
                    </Typography>
                  )}
                </Grid>

                <Grid item>
                  {user.premium && (
                    <>
                      <Typography
                        variant="caption"
                        color="primary"
                      >
                        <FormattedMessage id="app.premium" />
                      </Typography>
                      <br />
                    </>
                  )}

                  {user.isBlocked && (
                    <>
                      <Typography
                        variant="caption"
                        color="error"
                      >
                        <FormattedMessage id="app.user.isBlocked" />
                      </Typography>
                      <br />
                    </>
                  )}

                  {user.role === Roles.Agent ? user?.contractAgent?.jobTitle : <FormattedMessage id={`app.enuns.${user.role}`} />}
                </Grid>
              </Grid>
            }
          />
        </Card>

        <Tabs
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
          value={indexTab}
          onChange={(event, value) => setIndexTab(value)}
        >
          <Tab label={<FormattedMessage id="app.data" />} />
          <Tab label={<FormattedMessage id="app.lccontracts" />} />
        </Tabs>
      </FixedPageItem>

      {indexTab === 0 ? (
        <UserDetailVIew
          currentUser={currentUser}
          user={user}
          fetchUser={fetchUser}
          history={history}
          setLoading={setLoading}
          dispatch={dispatch}
        />
      ) : (
        <CardPageItem>
          <UserContractsOverview user={user} />
        </CardPageItem>
      )}
    </PageContainer>
  );
};

export default withRouter(React.memo(UserDetailPage));
